import { ref } from 'vue'
import { UserRole } from '@/enums/domain'

export interface NavItem {
    key:   string
    label: string
    icon:  string
    to:    string
    roles: string[]
    badge?:        boolean
    pendingBadge?: boolean
    // v1.16: also shown to a role-`user` account an admin granted report access.
    reportsGrant?: boolean
}

export interface NavSection {
    header?: string
    roles:   string[]
    items:   NavItem[]
}

const ALL = ['*']
const ADMIN = [UserRole.SuperAdmin, UserRole.ProbeAdmin]
const SA = [UserRole.SuperAdmin]
// Everyone who books — i.e. all roles except the read-only reporter (v1.16).
const BOOKERS = [UserRole.SuperAdmin, UserRole.ProbeAdmin, UserRole.User]
const REPORTS = [UserRole.SuperAdmin, UserRole.ProbeAdmin, UserRole.Reporter]

// Sidebar drawer state (mobile off-canvas).
export const sidebarOpen = ref(false)

export const navSections: NavSection[] = [
    {
        roles: ALL,
        items: [
            { key: 'dashboard',    label: 'nav.dashboard',    icon: 'i-lucide-layout-dashboard', to: '/dashboard',   roles: BOOKERS },
            { key: 'reservations', label: 'nav.reservations', icon: 'i-lucide-calendar-days',    to: '/rezervacije', roles: BOOKERS },
        ],
    },
    {
        header: 'nav.management',
        roles:  ADMIN,
        items: [
            { key: 'confirmations', label: 'nav.confirmations', icon: 'i-lucide-clipboard-check', to: '/potrditve',    roles: ADMIN, pendingBadge: true },
            { key: 'probeplan',     label: 'nav.probe_plan',    icon: 'i-lucide-arrow-left-right', to: '/menjave-sond', roles: ADMIN },
            { key: 'devices', label: 'nav.devices', icon: 'i-lucide-microscope',  to: '/naprave',    roles: ADMIN },
            { key: 'downtimes', label: 'nav.downtimes', icon: 'i-lucide-calendar-off', to: '/nedelovanja', roles: ADMIN },
            { key: 'probes',  label: 'nav.probes',  icon: 'i-lucide-magnet',       to: '/sonde',      roles: ADMIN },
            { key: 'users',   label: 'nav.users',   icon: 'i-lucide-users',        to: '/uporabniki', roles: SA },
            { key: 'groups',  label: 'nav.groups',  icon: 'i-lucide-users-round',  to: '/skupine',    roles: SA },
            { key: 'clients', label: 'nav.clients', icon: 'i-lucide-building-2',   to: '/stranke',    roles: SA },
            { key: 'quotas',  label: 'nav.quotas',  icon: 'i-lucide-gauge',        to: '/kvote',      roles: ADMIN },
            { key: 'reports', label: 'nav.reports', icon: 'i-lucide-chart-column', to: '/porocila',   roles: REPORTS, reportsGrant: true },
        ],
    },
    {
        header: 'nav.personal',
        roles:  ALL,
        items: [
            { key: 'notifications', label: 'nav.notifications', icon: 'i-lucide-bell',    to: '/obvestila',   roles: ALL, badge: true },
            { key: 'settings',      label: 'nav.settings',      icon: 'i-lucide-settings', to: '/nastavitve',  roles: ALL },
        ],
    },
]

// An item is visible by role, or — for report items — by the user's report grant.
export const canSeeItem = (item: NavItem, userRole: string | undefined, reportsGrant: boolean): boolean =>
    canSee(item.roles, userRole) || (!!item.reportsGrant && reportsGrant)

export const canSee = (roles: string[], userRole: string | undefined): boolean => {
    if (roles.includes('*')) return true
    if (!userRole) return false
    return roles.includes(userRole)
}
